import { useMemo, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { AtsCandidateDetailDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { DuplicateCandidatesAlert } from 'modules/ats/components/DuplicateCandidatesAlert'
import { DuplicateCandidatesSidepanel } from 'modules/ats/components/DuplicateCandidatesSidepanel'

import { CandidateSideContent } from './SideContent'

const CandidateDetailPage = () => {
  const { id } = useParams()
  const { pathname } = useLocation()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const [isDuplicatesSidepanelOpen, setIsDuplicatesSidepanelOpen] = useState(false)

  const candidateId = Number(id)

  const { loading, data } = useQuery(AtsCandidateDetailDocument, {
    variables: { id: candidateId, companyId: company.id },
  })

  const candidate = data?.ats.candidate

  const isDuplicateIdentificationEnabled =
    data?.features.duplicateIdentificationFeature.nodes[0]?.enabled ?? false

  const duplicates = useMemo(() => {
    const sourceDuplicates = data?.ats.source.nodes ?? []
    const matchedDuplicates = data?.ats.matched.nodes ?? []
    const allDuplicates = [...sourceDuplicates, ...matchedDuplicates]
    const dedupedDuplicates = new Map<number, (typeof allDuplicates)[number]>()

    for (const duplicate of allDuplicates) {
      dedupedDuplicates.set(duplicate.id, duplicate)
    }

    return Array.from(dedupedDuplicates.values())
  }, [data?.ats.source.nodes, data?.ats.matched.nodes])

  const hasDuplicates = isDuplicateIdentificationEnabled && duplicates.length > 0

  const isResumePath = pathname === navigation.ats.candidates.id.cv.fullPath({ id: candidateId })

  if (loading || !candidate) {
    return (
      <Box padding="s40" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Box flexDirection="row" width="full">
        <Box
          flexNoShrink
          height="full"
          overflowY="auto"
          overflowX="hidden"
          paddingY={isResumePath ? 's0' : 's20'}
          paddingX={isResumePath ? 's0' : 's24'}
          paddingLeft={isResumePath ? 's1' : 's24'}
          border={{ right: { style: 'solid', width: 's1', color: 'grey200' } }}
          style={{ width: '75%' }}
        >
          {hasDuplicates && (
            <Box paddingBottom="s16">
              <DuplicateCandidatesAlert onView={() => setIsDuplicatesSidepanelOpen(true)} />
            </Box>
          )}
          <Outlet context={{ candidate }} />
        </Box>
        <Box
          flexGrow
          height="full"
          overflowY="auto"
          paddingTop="s20"
          paddingRight="s16"
          paddingLeft="s8"
          paddingBottom="s24"
        >
          <CandidateSideContent data={candidate} />
        </Box>
      </Box>

      {hasDuplicates && (
        <DuplicateCandidatesSidepanel
          open={isDuplicatesSidepanelOpen}
          onClose={() => setIsDuplicatesSidepanelOpen(false)}
          currentCandidate={candidate}
          duplicates={duplicates}
        />
      )}
    </>
  )
}

export default CandidateDetailPage
